import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate } from 'react-router-dom'

import { useFetch } from '@reveni-io/reveni-ui'

import useRegions from '@/common/hooks/useRegions/useRegions'
import commonServices from '@/common/services/services'
import configurationServices from '@/configuration/services/services'
import routes from '@/Router/routes.constants'

import { getDeliveryMethodsPayload, getDeliveryMethodsWithNetOrderAmount } from './helpers'

const useDeliveryMethods = ({ storeId, setToastInfo }) => {
  const { t } = useTranslation()
  const location = useLocation()
  const navigate = useNavigate()
  const { regions: regionsData, loading: gettingRegions } = useRegions()
  const { data: carriersData, loading: fetchingCarriers } = useFetch(
    () => commonServices.getCarrierServices({ storeId, section: 'returns' }),
    {
      doInitialRequest: true,
    }
  )

  const {
    data: logisticsPreferences,
    fetchData: getLogisticsPreferences,
    loading: gettingLogisticsPreferences,
  } = useFetch(() => configurationServices.getStoreLogisticsPreferences({ storeId }), {
    doInitialRequest: true,
  })
  const { fetchData: editDeliveryMethods, loading: editingDeliveryMethods } = useFetch(
    configurationServices.editDeliveryMethods,
    {
      doInitialRequest: false,
    }
  )
  const { fetchData: deleteDeliveryMethod } = useFetch(configurationServices.deleteDeliveryMethod, {
    doInitialRequest: false,
  })
  const [deliveryMethods, setDeliveryMethods] = useState([])
  const [hasChanges, setHasChanges] = useState(false)
  const [deliveryMethodSelected, setDeliveryMethodSelected] = useState(null)
  const [showDeleteDeliveryMethodModal, setShowDeleteDeliveryMethodModal] = useState(false)

  const setInitValues = useCallback(() => {
    if (logisticsPreferences) {
      setDeliveryMethods(getDeliveryMethodsWithNetOrderAmount(logisticsPreferences?.delivery_methods))
    }

    setHasChanges(false)
  }, [logisticsPreferences])

  useEffect(() => {
    setInitValues()
  }, [setInitValues])

  const revertChanges = () => {
    setInitValues()
  }

  const onDeleteDeliveryMethod = async () => {
    try {
      await deleteDeliveryMethod({ storeId, deliveryMethodId: deliveryMethodSelected.id })
      setToastInfo({ show: true, message: t('Método de devolución eliminado'), type: 'success' })
      getLogisticsPreferences()
      setShowDeleteDeliveryMethodModal(false)
    } catch (error) {
      setToastInfo({ show: true, message: error.errorMessage, type: 'error' })
    }
  }

  const onEditDeliveryMethod = deliveryMethod => {
    navigate(routes.configurationPage.logistics.deliveryMethodDetailConfiguration.replace(':id', deliveryMethod.id), {
      state: {
        childPageTitle: deliveryMethod?.internal_name,
        selectedDeliveryMethod: deliveryMethod,
        previousLocation: location,
      },
    })
  }

  const onSave = async () => {
    try {
      await editDeliveryMethods({
        storeId,
        deliveryMethods: getDeliveryMethodsPayload(deliveryMethods),
      })

      setToastInfo({ show: true, message: t('Cambios guardados'), type: 'success' })

      await getLogisticsPreferences()
    } catch (error) {
      setToastInfo({ show: true, message: error.errorMessage, type: 'error' })
    }
  }

  const onDuplicateDeliveryMethod = deliveryMethod => {
    navigate(
      routes.configurationPage.logistics.deliveryMethodDetailConfiguration.replace(
        ':id',
        `duplicate-${deliveryMethod.id}`
      ),
      {
        state: { selectedDeliveryMethod: deliveryMethod, previousLocation: location },
      }
    )
  }

  const onCreate = () => {
    navigate(
      routes.configurationPage.logistics.deliveryMethodDetailConfiguration.replace(':id', 'new', {
        state: { previousLocation: location },
      })
    )
  }

  const handleChangeEnabled = (id, checked) => {
    const deliveryMethodsUpdated = deliveryMethods.map(dm => {
      if (dm.id === id) {
        return { ...dm, is_enabled: checked }
      }
      return dm
    })

    setDeliveryMethods(deliveryMethodsUpdated)
    setHasChanges(true)
  }

  const handleOnOpenDeleteDeliveryMethodModal = id => {
    const deliveryMethod = deliveryMethods.find(deliveryMethod => deliveryMethod.id === id)

    setDeliveryMethodSelected(deliveryMethod)
    setShowDeleteDeliveryMethodModal(true)
  }

  return {
    regions: regionsData,
    carriers: carriersData || [],
    fetchingCarriers,
    gettingRegions,
    deliveryMethods,
    setDeliveryMethods: params => {
      setDeliveryMethods(params)
      setHasChanges(true)
    },
    getLogisticsPreferences,
    gettingLogisticsPreferences,
    editDeliveryMethods,
    editingDeliveryMethods,
    deleteDeliveryMethod,
    logisticsPreferences,
    revertChanges,
    hasChanges,
    setHasChanges,
    onDeleteDeliveryMethod,
    deliveryMethodSelected,
    setDeliveryMethodSelected,
    showDeleteDeliveryMethodModal,
    setShowDeleteDeliveryMethodModal,
    onEditDeliveryMethod,
    onSave,
    onDuplicateDeliveryMethod,
    onCreate,
    handleChangeEnabled,
    handleOnOpenDeleteDeliveryMethodModal,
  }
}

export default useDeliveryMethods
